import React, { useState, useRef, useEffect } from 'react';
import { Loader, Table, Modal, Button } from 'semantic-ui-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import WarningOutlineIcon from '@mui/icons-material/WarningAmber';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { withStyles } from '@mui/styles';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ReportIcon from '@mui/icons-material/Report';

import { useUserState } from '../../context/UserContext';
import Arrow from '../../images/Arrow.svg';
import ErrorMessage from '../../components/Error/Error';
import { getNextStep, listEvents, restatePayloadEvent } from '../../api/api';
import { useDidMountEffect } from '../../utils/utils';

// Event Models
import { DataCustodyRequestedEventModel } from '../../models/events/DataCustodyRequestedEvent';
import { DataCustodyReceivedEventModel } from '../../models/events/DataCustodyReceivedEvent';
import { ContextualizationRequestEventModel } from '../../models/events/ContextualizationRequestEvent';
import ContextualizationEventModel from '../../models/events/ContextualizationEvent';
import IDGeneratorEventModel from '../../models/events/IDGeneratorEvent';
import { RegistrationEventModel } from '../../models/events/RegistrationEvent';
import { AssetRegistrationEventModel } from '../../models/events/AssetRegistrationEvent';

// PDF file to export
import EventsToPDF from '../../pdfs/EventsSteps.jsx';

import NotableEventManualStep from './NotableEventManualStep.jsx';
import GeneralManualStep from './GeneralManualStep.jsx';
import ContextualizationManualStep from './ContextualizationManualStep';
import PayloadBuilder from '../../components/PayloadBuilder/PayloadBuilder';
import DetailItem from '../../components/DetailItem/DetailItem';
import { readableStepName, escapeEmptyDetail } from '../../utils/utils';
import {useAuth0} from "@auth0/auth0-react";

const CustomTooltip = withStyles({
  tooltip: {
    fontSize: '12px',
    top: '-6px',
  },
})(Tooltip)

const Payloads = () => {
  const navigate = useNavigate();
  const user = useUserState();
  const params = useParams();
  const search = useLocation().search;

  const ref = useRef([]);
  const hiddenRef = useRef();
  const pdfBtnRef = useRef([]);
  const jsonBtnRef = useRef([]);

  const [eventSelected, setEventSelected] = useState();
  const [notableEventSelected, setNotableEventSelected] = useState();
  const [notableIconClicked, setNotableIconClicked] = useState(false);
  const [workflow, setWorkflow] = useState();
  const [payloadId, setPayloadId] = useState();
  const [payload, setPayload] = useState();
  const [events, setEvents] = useState([]);
  const [workflowStep, setWorkflowStep] = useState();
  const [restatementEvent, setRestatementEvent] = useState(false);
  const [notableEventModalOpen, setNotableEventModalOpen] = useState(false);
  const [generalManualStepModalOpen, setGeneralManualStepModalOpen] = useState(false);
  const [contextualizationManualStepModalOpen, setContextualizationManualStepModalOpen] = useState(false);
  const [restatementModalOpen, setRestatementModalOpen] = useState(false);
  const [payloadCompleted, setPayloadCompleted] = useState(false);
  const [payloadActive, setPayloadActive] = useState(false);
  const [payloadTransacted, setPayloadTransacted] = useState(false);
  const [payloadRetired, setPayloadRetired] = useState(false);
  const [notableErrorPosition, setNotableErrorPosition] = useState();
  const [currentStep, setCurrentStep] = useState();
  const [iconsPositions, setIconsPositions] = useState([]);
  const [activeEvents, setActiveEvents] = useState([]);
  const [activeNotableEvents, setActiveNotableEvents] = useState([]);
  const [relatedEventSelected, setRelatedEventSelected] = useState();
  const [eventsToExportJSON, setEventsToExportJSON] = useState();
  const [eventsToPDFActive, setEventsToPDFActive] = useState();
  const [eventsToPDFInactive, setEventsToPDFInactive] = useState();
  const [loading, setLoading] = useState(true);
  const [relatedEventSelectedPosition, setRelatedEventSelectedPosition] = useState();
  const [errorStatus, setErrorStatus] = useState();
  const [errorDetail, setErrorDetail] = useState();
  const [hasError, setHasError] = useState(false);
  let currentOrg = JSON.parse(localStorage.getItem('current_org'));
  const { getAccessTokenSilently, logout } = useAuth0();

  useEffect(() => {
    window.scrollTo(0, 0);
    window.localStorage.setItem('main_tab', 'workflows');
    console.log('CONTEXT_API_UPDATED -> ', user)

    let payloadId = params.payloadId;
    setPayloadId(payloadId);

    listEvents({ logout, getAccessTokenSilently }, payloadId)
      .then((res) => {
        setEvents(res.data);
        switch (res.data.status) {
          case 'ACTIVE':
            setPayloadActive(true)
            break;
          case 'COMPLETED':
            setPayloadCompleted(true)
            break;
          case 'TRANSACTED':
            setPayloadTransacted(true)
            break;
          case 'RETIRED':
            setPayloadRetired(true)
            break;
          default:
        }

        if (res.data.events.length > 0) {
          res.data.events.forEach(el => {
            if(el.status ==='ACTIVE') {
              setPayload(el.payload);
            }
          })
        }

        // checking for restataments
        if (res.data.restatementEvents.length) {
          setIconsPositions([]);
          setRestatementEvent(true);
        }

        // setting active events array state
        let activeArr = [];
        res.data.events?.forEach((s) => {
          if (s.status === 'ACTIVE') {
            activeArr.push(s);
          }
        })
        setActiveEvents(activeArr);

        // setting active notable events array state
        let activeNotableArr = [];
        res.data.notableEvents?.forEach((s) => {
          if (s.status === 'ACTIVE') {
            activeNotableArr.push(s);
          }
        })
        setActiveNotableEvents(activeNotableArr);

        if(res.data.status === 'ACTIVE') {
          getNextStep({ logout, getAccessTokenSilently }, payloadId)
            .then((res) => {
              if(res.data && res.data.order !== undefined)
                setCurrentStep(res.data.order);
              else 
                setCurrentStep(null);
            })  
        }

        setWorkflow(res.data.workflowDefinition);
        
      }).then(()=> {
        const format = new URLSearchParams(search).get('format');
        if (format !== '')
          downloadFileFromParam(format);
      })
      .catch((err) => {
        setHasError(true);
        setErrorStatus(err.response.data.status);
        setErrorDetail(err.response.data.detail);
      })
      .finally(() => {
        setLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useDidMountEffect(() => {
    let firstStepAutoSelected = false;

    events?.events?.forEach((e, index) => {
      if (!firstStepAutoSelected && e.status === 'ACTIVE') {
        setEventSelected(e);
        firstStepAutoSelected = true;
      }
    })
  }, [events])

  // getting notable events positions
  useDidMountEffect(() => {
    let arr = [];
    let activeNotableEventsArr = activeNotableEvents;

    activeEvents?.forEach((event, index) => {
      activeNotableEventsArr?.forEach((notable, i) => {
        if (event.createdAt < notable.createdAt) {
          arr.push({ position: index + 1, event: notable });
        }
      })
    })

    if (activeEvents[0] === undefined) {
      activeNotableEventsArr?.forEach((notable, i) => {
        arr.push({ position: 0, event: notable });
      })
    }

    let uniqueObjects = [ ...new Map(arr.map((item) => [item.event.eventId, item])).values() ];
    
    let allEvents = events?.events?.concat( events.notableEvents, events.restatementEvents );
    allEvents?.forEach((a) => {
      if (a['@type']) {
        a.eventType = 'Event';
      } else if (a.isError !== undefined) {
        a.eventType = 'Notable Event';
      } else {
        a.eventType = 'Restatement Event';
      }
    });

    let eventsToPDFFormatted = allEvents?.sort( (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
    let eventsToPDFFormattedActive = [];
    let eventsToPDFFormattedInactive = [];

    eventsToPDFFormatted?.forEach((ev) => {
      if (ev.status === 'ACTIVE') {
        eventsToPDFFormattedActive.push(ev);
      } else {
        eventsToPDFFormattedInactive.push(ev);
      }
    })

    setEventsToExportJSON(eventsToPDFFormatted)
    setEventsToPDFActive(eventsToPDFFormattedActive)
    setEventsToPDFInactive(eventsToPDFFormattedInactive)

    setEventsToExportJSON(eventsToPDFFormatted);
    setEventsToPDFActive(eventsToPDFFormattedActive);
    setEventsToPDFInactive(eventsToPDFFormattedInactive);

    setIconsPositions(uniqueObjects);
  }, [activeEvents, activeNotableEvents, events])

  // getting notable event error position
  useDidMountEffect(() => {
    workflow?.steps.forEach((w, index) => {

      iconsPositions?.forEach((i) => {
        if (index === i.position && i.event.isError) {
          setNotableErrorPosition(index);
        }
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iconsPositions, workflow])

  // close events list if click outside
  useDidMountEffect(() => {
    const checkIfClickedOutside = (e) => {
      if ( notableIconClicked !== undefined && ref.current[0] && !ref.current[0]?.contains(e.target) ) {
        setNotableIconClicked(false);
      }
    }

    document.addEventListener('mousedown', checkIfClickedOutside);

    return () => {
      document.removeEventListener('mousedown', checkIfClickedOutside);
    }
  }, [notableIconClicked])

  const downloadFileFromParam = (format) => {
    setTimeout(() => {

      switch (format) {
        case 'pdf':
          pdfBtnRef.current.click();
          break;
        case 'json':
          jsonBtnRef.current.click();
          break;
        default:
      }

    }, 2000);
  }

  const cleanRelatedEventSelected = () => {
    setRelatedEventSelected();
    setRelatedEventSelectedPosition();
  }

  const selectEvent = (step) => {
    // if ( window.pageYOffset + window.innerHeight <= hiddenRef.current?.offsetTop ) {
      ref.current.scrollIntoView();
    // }

    setEventSelected(step);
    setNotableEventSelected();
    cleanRelatedEventSelected();
  }

  const selectNotableEvent = (notableEvent) => {
    // if ( window.pageYOffset + window.innerHeight <= hiddenRef.current?.offsetTop ) {
      ref.current.scrollIntoView();
    // }

    setNotableEventSelected(notableEvent);
    setNotableIconClicked(false);
    cleanRelatedEventSelected();
  }

  const getRelatedEvent = (relatedEventId) => {
    setRelatedEventSelected(relatedEventId);
    events?.events.forEach((r) => {
      if (relatedEventId === r.eventId) {
        setRelatedEventSelectedPosition(r.order);
      }
    });
  }

  const testEventPermission = (custodians) => {
    return custodians?.find(custodian => custodian.orgId === currentOrg.id) !== undefined
  }

  const checkEventPosition = (index) => {
    let icon = '';
    let iconCount = 0;
    let eventsList = [];

    iconsPositions?.forEach((p) => {
      if (iconCount === 0 && p.position === index) {
        icon = (
          <span className='arrow-icon'>
            {p.event.isError ? (
              <CustomTooltip placement={'top'} arrow title='See Error details'>
                <WarningOutlineIcon
                  className='error'
                  onClick={() => {
                    selectNotableEvent(p.event)
                  }}
                ></WarningOutlineIcon>
              </CustomTooltip>
            ) : (
              <CustomTooltip
                placement={'top'}
                arrow
                title='See Notable Event(s) details'
              >
                <HelpOutlineIcon
                  className='warning'
                  onClick={() => {
                    selectNotableEvent(p.event)
                  }}
                ></HelpOutlineIcon>
              </CustomTooltip>
            )}
          </span>
        );
        eventsList.push(p);
        iconCount++;
      } else if (iconCount > 0 && p.position === index) {
        let headings = ['Party Name', 'Event Related', 'Created at'];
        icon = (
          <>
            <span className='arrow-icon'>
              {p.event.isError ? (
                <CustomTooltip
                  placement={'top'}
                  arrow
                  title='See Error details'
                >
                  <WarningOutlineIcon
                    className='error'
                    onClick={() => {
                      selectNotableEvent(p.event)
                    }}
                  ></WarningOutlineIcon>
                </CustomTooltip>
              ) : (
                ''
              )}

              <CustomTooltip
                placement={'top'}
                arrow
                title='See Notable Event(s) details'
              >
                <HelpOutlineIcon
                  className='warning'
                  onClick={() => {
                    notableListBox(p.position)
                  }}
                ></HelpOutlineIcon>
              </CustomTooltip>
            </span>
            <div
              className={`eventslist-wrapper ${
                notableIconClicked === p.position ? 'clicked' : ''
              }
                ${p.event.isError ? 'both-icons' : ''}
              `}
            >
              <div className={`eventslist`}>
                <Table>
                  <Table.Header>
                    <Table.Row>
                      {headings.map((h, index) => {
                        return (
                          <Table.HeaderCell key={index}>{h}</Table.HeaderCell>
                        )
                      })}
                      <CloseIcon className="close_pop" onClick={() => setNotableIconClicked(false)} />
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {iconsPositions?.map((h) => {
                      if (
                        h.position === p.position &&
                        h.event.isError === false
                      ) {
                        return (
                          <Table.Row
                            onClick={() => {
                              selectNotableEvent(h.event)
                            }}
                            className={h.error ? 'error' : ''}
                            key={h.partyId}
                          >
                            <Table.Cell>{h.event.partyName}</Table.Cell>
                            <Table.Cell>{h.event.relatedEventId}</Table.Cell>
                            <Table.Cell>
                              {h.event.createdAt.split('T', 1) +
                                ' ' +
                                h.event.createdAt
                                  .split('T', 2)[1]
                                  .split('.', 1) +
                                ' (GMT)'}
                            </Table.Cell>
                          </Table.Row>
                        )
                      }
                      // eslint-disable-next-line array-callback-return
                      return
                    })}
                  </Table.Body>
                </Table>
              </div>
            </div>
          </>
        );
        eventsList.push(p);
      }
    })

    return icon;
  }

  const notableListBox = (e) => {
    if (notableIconClicked !== false)
      setNotableIconClicked(false);
    else 
      setNotableIconClicked(e)

  }

  const renderFlowchart = () => {
    let stepsQty = workflow?.steps?.length;
    return (
      <>
        <div className='workflow-wrapper'>
          {workflow?.steps.map((step, index) => (
            <div className='workflow-step' key={index}>
              <div
                className={` step-wrapper 
              ${(index + 1) % 5 === 0 && index !== 1 ? 'last' : ''} 
              ${
                (index + 2) % 5 === 0 && index !== 1 ? 'eventlist-to-left' : ''
              } 
              `}
              >
                <figure>
                  {index > 0 && (index + 1) % 6 !== 0 &&
                    <span
                    className={`${index < stepsQty ? 'arrow' : ''}`}
                  ></span>
                  }
                  {checkEventPosition(index)}
                </figure>
                <div
                  className={`step 
                  ${index < notableErrorPosition ? 'completed-error' : ''}
                  ${
                    index < currentStep - 1 || payloadCompleted
                      ? 'completed'
                      : ''
                  }
                  ${index === currentStep - 1 ? 'current' : ''}  
                  ${
                    (step.order === eventSelected?.order &&
                      !notableEventSelected) ||
                    relatedEventSelectedPosition === step.order
                      ? 'selected'
                      : ''
                  }
                  ${payloadTransacted ? 'transacted' : ''}
                  ${payloadRetired ? 'retired' : ''}
                  ${(index === currentStep - 1) &&
                    step.isManual
                    ? 
                    'manual' : ''}
                    ${(payloadActive && index >= currentStep - 1) ||
                      (index >= notableErrorPosition) ? 'next' : ''}  
                  `}
                  onClick={() => {
                    if ((index === currentStep - 1) &&
                        step.isManual &&
                        ((step['@type'] === 'DataCustodyRequestedStep' && testEventPermission(step.newCustodians)) ||
                        (step['@type'] === 'DataCustodyReceivedStep' && testEventPermission(step.newCustodians)) ||
                        (step['@type'] === 'ContextualizationRequestStep' && testEventPermission(step.requestingStakeholders)) ||
                        (step['@type'] === 'RegistrationStep' && testEventPermission(step.oldCustodians)) ||
                        (step['@type'] === 'AssetRegistrationStep' && testEventPermission(step.oldCustodians)))
                      ){
                      setWorkflowStep(step);
                      setGeneralManualStepModalOpen(true);
                    } else if((index === currentStep - 1) &&
                        step.isManual &&
                        ((step['@type'] === 'ContextualizationStep' && testEventPermission(step.stakeholders)) ||
                        (step['@type'] === 'IDGeneratorStep' && testEventPermission(step.stakeholders))
                      )){
                      setWorkflowStep(step);
                      setContextualizationManualStepModalOpen(true);
                    } else {
                      selectEvent(step)
                    }
                  }}
                >
                  <p>
                    { step.title ?
                      step.title
                    :
                      readableStepName(step['@type'])
                    }
                    {<span><br/>({step.isManual ? 'Manual' : 'Automated'})</span>}
                  </p>
                </div>
              </div>
              {(index + 1) % 5 === 0 && index !== 1 && index + 1 < stepsQty ? (
                <figure
                  className={`${
                    (index + 1) % 5 === 0 && index !== 1 && index + 1 < stepsQty
                      ? 'arrow-long-wrapper'
                      : ''
                  }`}
                >
                  <span
                    className={`${
                      (index + 1) % 5 === 0 && index !== 1 ? 'arrow-long' : ''
                    }`}
                  ></span>
                </figure>
              ) : (
                ''
              )}
            </div>
          ))}
        </div>
      </>
    );
  }

  const getEventObj = (step) => {
    let eventType = '';
    let eventObj = '';
    events.events?.forEach((a) => {
      if (step.order === a.order && a.status === 'ACTIVE') {
        eventType = `${a['@type']}`;
        eventObj = a;
      } else if (a.eventId === step) {
        eventType = `${a['@type']}`;
        eventObj = a;
      }
    })

    switch (eventType) {
      case 'DataCustodyRequestedEvent':
        return <>{DataCustodyRequestedEventModel(eventObj)}</>;
      case 'DataCustodyReceivedEvent':
        return <>{DataCustodyReceivedEventModel(eventObj)}</>;
      case 'ContextualizationRequestEvent':
        return <>{ContextualizationRequestEventModel(eventObj)}</>;
      case 'ContextualizationEvent':
        return <>{ContextualizationEventModel(eventObj)}</>;
      case 'IDGeneratorEvent':
        return <>{IDGeneratorEventModel(eventObj)}</>;
      case 'RegistrationEvent':
        return <>{RegistrationEventModel(eventObj)}</>;
      case 'AssetRegistrationEvent':
        return <>{AssetRegistrationEventModel(eventObj)}</>;
      default:
        console.log('Step not set on the FE -> check Payloads.jsx file');
        break;
    };
  }


  const downloadFile = ({ data, fileName, fileType }) => {
    const blob = new Blob([data], { type: fileType })

    const a = document.createElement('a')
    a.download = fileName
    a.href = window.URL.createObjectURL(blob)
    const clickEvt = new MouseEvent('click', {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    a.dispatchEvent(clickEvt);
    a.remove();
  }

  // export events json
  const EventsToJson = (e) => {
    e.preventDefault();

    let eventsToJSONSorted = eventsToExportJSON?.sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)
    );

    downloadFile({
      data: JSON.stringify(eventsToJSONSorted),
      fileName: (payloadId).replaceAll('.','_') + '.json',
      fileType: 'text/json',
    });
  }

  const restatePayload = () => {
    setLoading(true);
    setRestatementModalOpen(false);
    restatePayloadEvent({ logout, getAccessTokenSilently }, payloadId)
    .then((res) => {
      navigate(0);
    })
    .catch((err) => {
      setHasError(true);
      setErrorStatus(err.response.status);
    })
  }

  return (
    <div className='details'>
      {loading ? (
        <div className='empty-table'>
          <Loader active indeterminate size='small' />
        </div>
      ) : (
        <>
          {hasError ? (
            <ErrorMessage statusCode={errorStatus} detail={errorDetail}></ErrorMessage>
          ) : (
            <>
              <section className='search-result'>
                <p
                  className='link'
                  onClick={() => {
                    navigate('/')
                  }}
                >
                  Workflows
                </p>
                <p>
                  <img src={Arrow} alt='arrow'></img>
                </p>
                <p
                  className='link'
                  onClick={() => {
                    navigate(`/workflows/${workflow.riid}`)
                  }}
                >
                  {workflow?.title}
                </p>
                <p>
                  <img src={Arrow} alt='arrow'></img>
                </p>
                <p>
                  {'Payload Id '}
                  {payloadId}
                </p>
              </section>
              <Box
                sx={{
                  width: '100%',
                  border: '1px solid lightgrey',
                  borderRadius: '4px',
                  padding: '20px',
                  marginBottom: '20px',
                  display: 'block',
                }}
              >
                <div className='top-content'>
                  <p className='details-title'>
                    <b>Payload Id {payloadId}</b>
                  </p>
                  <div className='subtitles'>
                    <div>
                      <p>Notable Event(s)</p>
                      <HelpOutlineIcon className='warning'></HelpOutlineIcon>
                    </div>
                    <div>
                      <p>Notable Event Error</p>
                      <WarningOutlineIcon className='error'></WarningOutlineIcon>
                    </div>
                  </div>
                </div>
                {iconsPositions ? (
                  <div className='flowchart'>{renderFlowchart()}</div>
                ) : (
                  ''
                )}
                <div className='bottom-content'>
                  {restatementEvent ? (
                    <>
                      <ReportIcon className='restate'></ReportIcon>
                      <p>Workflow Restated</p>
                    </>
                  ) : (
                    ''
                  )}
                </div>
                {events.events ? (
                  <div className='btns-wrapper'>
                    <section className='action-btn-wrapper'>
                      { events.status === 'ACTIVE' &&
                        <Tooltip placement={'top'} arrow title='Add Notable Event'>
                          <button onClick={() => setNotableEventModalOpen(true)}>Add Notable Event</button>
                        </Tooltip>
                      }
                      { events.status !== 'TRANSACTED' && events.status !== 'RETIRED' &&
                        <Tooltip placement={'top'} arrow title='Restate Payload'>
                          <button onClick={() => setRestatementModalOpen(true)}>Restate Payload</button>
                        </Tooltip>
                      }
                    </section>
                    <span ref={ref}></span>
                    <section className='export-btn-wrapper export-btn-wrapper-halfrow'>
                      <PDFDownloadLink
                        document={EventsToPDF(
                          eventsToPDFActive,
                          eventsToPDFInactive,
                          payloadId
                        )}
                        fileName={(payloadId).replaceAll('.','_')}
                      >
                        <Tooltip placement={'top'} arrow title='Export PDF'>
                          <button ref={pdfBtnRef} className='sm-btn'> pdf </button>
                        </Tooltip>
                      </PDFDownloadLink>

                      <Tooltip placement={'top'} arrow title='Export JSON'>
                        <button
                          ref={jsonBtnRef}
                          className='sm-btn json'
                          onClick={EventsToJson}
                        >
                          {'{ }'}
                        </button>
                      </Tooltip>
                    </section>
                  </div>
                ) : (
                  ''
                )}
              </Box>

              {(eventSelected && !notableEventSelected) ||
              relatedEventSelected ? (
                <>
                  <span ref={hiddenRef}></span>
                    {getEventObj(
                    relatedEventSelected
                      ? relatedEventSelected
                      : eventSelected
                  )}
                </>
              ) : (
                ''
              )}


              {notableEventSelected && !relatedEventSelected ? (
                <>
                  <Box
                    sx={{
                      width: '100%',
                      border: '1px solid lightgrey',
                      borderRadius: '4px',
                      padding: '20px',
                      marginBottom: '20px',
                      display: 'block',
                    }}>
                      <p className='details-title'>
                        <b>
                          {notableEventSelected.isError ? (
                            <b>Notable Event Error</b>
                          ) : (
                            <b>Notable Event</b>
                          )}
                        </b>
                      </p>
                      <DetailItem title='Party Id' content={escapeEmptyDetail(notableEventSelected.partyId)} copy={notableEventSelected.partyId} />
                      <DetailItem title='Party Name' content={escapeEmptyDetail(notableEventSelected.partyName)} />
                      <DetailItem title='Payload Id' content={escapeEmptyDetail(notableEventSelected.payloadId)} copy={notableEventSelected.payloadId} />
                      <DetailItem title='Related Event' content={escapeEmptyDetail(notableEventSelected.relatedEventId)} execFunction={() => getRelatedEvent(notableEventSelected.relatedEventId)} />
                      <DetailItem title='Created at' content={notableEventSelected.createdAt.split('T', 1) + ' ' + notableEventSelected.createdAt.split('T', 2)[1].split('.', 1)+' (GMT)'} />
                      <DetailItem title='Reason' content={escapeEmptyDetail(notableEventSelected.content)} />
                    </Box>
                    <Box
                      sx={{
                        width: '100%',
                        border: '1px solid lightgrey',
                        borderRadius: '4px',
                        padding: '20px',
                        marginBottom: '20px',
                        display: 'block',
                      }}>
                      <PayloadBuilder payload={notableEventSelected.payload} />
                    </Box>
                  </>
              ) : (
                ''
              )}
              { notableEventModalOpen &&
                <NotableEventManualStep 
                  open={notableEventModalOpen}
                  onClose={() => setNotableEventModalOpen(false)}
                  payloadId={payloadId}
                  payload={payload}
                />
              }
              { generalManualStepModalOpen &&
                <GeneralManualStep 
                  open={generalManualStepModalOpen}
                  onClose={() => setGeneralManualStepModalOpen(false)}
                  payloadId={payloadId}
                  payload={payload}
                  workflowStep={workflowStep}
                />
              }
              { contextualizationManualStepModalOpen &&
                <ContextualizationManualStep 
                  open={contextualizationManualStepModalOpen}
                  onClose={() => setContextualizationManualStepModalOpen(false)}
                  payloadId={payloadId}
                  payload={payload}
                  workflowStep={workflowStep}
                />
              }
              { restatementModalOpen &&
                <Modal
                  open={restatementModalOpen}
                  onClose={() => setRestatementModalOpen(false)}
                  >
                  <Modal.Header>Restate Payload</Modal.Header>
                  <Modal.Content>
                    Are you sure you want to restate payload: '{payloadId}' ?
                  </Modal.Content>
                  <Modal.Actions>
                    <Button onClick={() => setRestatementModalOpen(false)}>
                      Cancel
                    </Button>
                    <Button negative onClick={restatePayload}>
                      Submit
                    </Button>
                  </Modal.Actions>
                </Modal>
              }
            </>
          )}
        </>
      )}
    </div>
  )
}

export default Payloads
